import classNames from 'classnames'
import KeyCode from 'lib/util/KeyCode'
import { Placement } from 'popper.js'
import React, { HTMLAttributes, ReactNode, useEffect, useRef } from 'react'
import ReactDom from 'react-dom'
import { Popper } from 'react-popper'

export interface DropdownMenuProps extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode
  className?: string
  flip?: boolean
  offset?: string
  placement?: Placement
  onRequestClose: ()=> void
}

export default function DropdownMenu(props: DropdownMenuProps): JSX.Element {
  const {
    children,
    className,
    flip = false,
    placement = 'bottom-end',
    offset = '0',
    onRequestClose,
    ...otherProps
  } = props
  const mergedClassName = classNames('dropdown-menu', className)
  const containerEl = useRef(null)

  function handleClickOutside(event: MouseEvent): void {
    if (containerEl && containerEl.current && !containerEl.current.contains(event.target as Node)) {
      onRequestClose && onRequestClose()
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.keyCode === KeyCode.ESC) {
      onRequestClose && onRequestClose()
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return function cleanup() {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  })

  return ReactDom.createPortal(
    <div ref={containerEl}>
      <Popper
        placement={placement}
        modifiers={{ flip: { enabled: flip }, offset: { offset }, preventOverflow: { boundariesElement: 'window' } }}>
        {({ ref, style, placement }) => (
          <ul
            className={mergedClassName}
            onClick={onRequestClose}
            ref={ref}
            style={style}
            data-placement={placement}
            {...otherProps}>
            {children}
          </ul>
        )}
      </Popper>
    </div>,
    document.getElementById('portals')
  )
}
